import { computed, reactive, ref, onMounted, nextTick, ComponentPublicInstance, watch } from 'vue';
import dayjs from 'dayjs';
import { useInternational } from '@/common/locale';
import { getTzTimeNow, secondsToPeriod } from '@/common/utils/time.utils';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { returnMatchingFormat } from '@/common/utils/core/time/parse';
import { ReadOnlyTimes } from '@/types/common.types';
import type { TimezoneName } from '@/common/utils';

dayjs.extend(customParseFormat);

export interface Props {
  // [fromTime, toTime]
  dateTimePeriod: [string, string];
  // max range time limit (seconds)
  maxRangeInSec?: number;
  // min range time limit (seconds)
  minRangeInSec?: number;
  // false 일 경우, 캘린더 열 때마다 시간은 00:00:00 ~ 23:59:59로 초기화 (dsp-15331)
  allowTime?: boolean;
  // 최소 선택 interval (ex. 1시간 일 경우, 1시간으로 딱 나눠 떨어지는 기간만 선택 가능)
  minimumIntervalInSec?: number;
  readonlyTimes?: ReadOnlyTimes;
  // 해당 날짜 이전 날짜는 disable
  disabledSelectedBeforeDay?: string;
  allowFutureDates?: boolean;
  // day 일 땐, 날짜만 표현
  timeUnit?: 'day' | 'sec';
  /** 시간 계산·표시 기준 타임존 */
  timezone?: TimezoneName;
}

export interface Emit {
  (e: 'update:isShow', value: boolean): void;
  (e: 'clickCancel'): void;
  (e: 'clickOk', value: string[]): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const FULL_DAY_RANGE = ['00:00:00', '00:00:00'];
  const rangeCalendarRef = ref();
  const now = getTzTimeNow();

  let from = now.startOf('day');
  const fromFormat = returnMatchingFormat(props.dateTimePeriod?.[0]);
  if (fromFormat) {
    from = dayjs(props.dateTimePeriod?.[0], fromFormat);
  }

  let to = now.endOf('day');
  const toFormat = returnMatchingFormat(props.dateTimePeriod?.[1]);
  if (toFormat) {
    to = dayjs(props.dateTimePeriod?.[1], toFormat);
  }

  const startDate = ref(from.toDate());
  const endDate = ref(to.toDate());
  const startDateString = computed(() => dayjs(startDate.value).format('YYYY.MM.DD'));
  const endDateString = computed(() =>
    endDate.value ? dayjs(endDate.value).format('YYYY.MM.DD') : '',
  );

  const fromTime = from.format('HH:mm:ss');
  const toTime = to.format('HH:mm:ss');

  const timePickerValues = ref(props.allowTime ? [fromTime, toTime] : [...FULL_DAY_RANGE]);

  const startDateTime = computed(() =>
    dayjs(`${startDateString.value} ${timePickerValues.value[0]}`),
  );
  const endDateTime = computed(() =>
    dayjs(endDate.value ? `${endDateString.value} ${timePickerValues.value[1]}` : undefined),
  );

  const onlyShowDate = computed(() => props.timeUnit === 'day');

  const getNow = () => (props.timezone ? dayjs.tz(undefined, props.timezone) : getTzTimeNow());

  const isEndDateTomorrowBoundary = computed(() => {
    if (props.allowFutureDates || !endDate.value) {
      return false;
    }

    const tomorrowStart = getNow().add(1, 'day').startOf('day');
    return dayjs(endDate.value).isSame(tomorrowStart, 'day');
  });

  const endTimeReadonlyTimes = computed<ReadOnlyTimes>(() => {
    if (!isEndDateTomorrowBoundary.value) {
      return (
        props.readonlyTimes ?? {
          hour: false,
          minute: false,
          second: false,
        }
      );
    }

    return {
      hour: true,
      minute: true,
      second: true,
    };
  });

  const diffFromTo = computed(() => endDateTime.value.diff(startDateTime.value, 'second'));
  const isMoreThanMaxRange = computed(() => {
    if (!props.maxRangeInSec || props.maxRangeInSec < 0) return false;
    return diffFromTo.value > props.maxRangeInSec;
  });

  const isLessThanMinRange = computed(() => {
    if (!props.minRangeInSec || props.minRangeInSec < 0) return false;
    return diffFromTo.value < props.minRangeInSec;
  });

  const isNotTimeDivisibleByInterval = computed(() => {
    if (!props.minimumIntervalInSec || props.minimumIntervalInSec < 0) return false;
    return (diffFromTo.value + 1) % props.minimumIntervalInSec !== 0;
  });

  const isRangeInToday = computed<boolean>(() => {
    const now = getNow();
    const todayStart = now.startOf('day');
    const todayEnd = now.endOf('day');

    return (
      startDateTime.value.isSame(todayStart, 'seconds') &&
      endDateTime.value.isSame(todayEnd, 'seconds')
    );
  });

  const validTimeFormatExp = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  const timeValidationInfo = reactive({
    fromTime: computed(() => {
      if (startDateTime.value > endDateTime.value) {
        return t('NOTI.VALIDATION.TIME_RANGE');
      }
      if (!validTimeFormatExp.test(timePickerValues.value[0])) {
        return t('WORD.ERROR');
      }
      if (isMoreThanMaxRange.value) {
        const period = secondsToPeriod(props.maxRangeInSec!);
        return t('MESSAGE.VALIDATION_GUIDE', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }
      if (isLessThanMinRange.value) {
        const period = secondsToPeriod(props.minRangeInSec!);
        return t('MESSAGE.VALIDATION_GUIDE_MIN', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }
      if (isNotTimeDivisibleByInterval.value) {
        const period = secondsToPeriod(props.minimumIntervalInSec!);
        return t('MESSAGE.VALIDATION_INTERVAL', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }

      return '';
    }),
    toTime: computed(() => {
      if (startDateTime.value > endDateTime.value) {
        return t('NOTI.VALIDATION.TIME_RANGE');
      }
      if (!validTimeFormatExp.test(timePickerValues.value[1])) {
        return t('WORD.ERROR');
      }
      if (isMoreThanMaxRange.value) {
        const period = secondsToPeriod(props.maxRangeInSec!);
        return t('MESSAGE.VALIDATION_GUIDE', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }
      if (isLessThanMinRange.value) {
        const period = secondsToPeriod(props.minRangeInSec!);
        return t('MESSAGE.VALIDATION_GUIDE_MIN', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }
      if (isNotTimeDivisibleByInterval.value) {
        const period = secondsToPeriod(props.minimumIntervalInSec!);
        return t('MESSAGE.VALIDATION_INTERVAL', {
          value: t(`WORD.${period.locale}`, { value: period.value }),
        });
      }

      return '';
    }),
  });

  const onClickResetDateTimePicker = () => {
    const today = (props.timezone ? dayjs.tz(undefined, props.timezone) : dayjs.tz()).startOf(
      'day',
    );
    const tomorrow = today.add(1, 'day');

    startDate.value = today.toDate();
    endDate.value = tomorrow.toDate();
    timePickerValues.value = [...FULL_DAY_RANGE];

    rangeCalendarRef.value?.setCalendarMonthYear({
      month: today.month(),
      year: today.year(),
    });
  };

  const END_OF_DAY_IN_SEC = 24 * 60 * 60 - 1; // 23:59:59

  // 범위 선택이 완료 되었는가
  const isRangeSelectionComplete = (newEnd: Date | undefined, oldEnd: Date | undefined) =>
    !oldEnd && !!newEnd && !!startDate.value;

  // 시작/종료가 같은 날짜인가
  const isSameDaySelection = (dateA: Date, dateB: Date) => dayjs(dateA).isSame(dayjs(dateB), 'day');

  // 내일 경계 더블 클릭인가
  const isTomorrowBoundarySelection = (date: Date) =>
    !props.allowFutureDates && dayjs(date).isSame(getNow().add(1, 'day').startOf('day'), 'day');

  // 같은 날짜 시 적용할 범위(초) 계산
  const calcSameDayRangeSec = () => {
    const maxSec =
      props.maxRangeInSec && props.maxRangeInSec > 0
        ? Math.min(props.maxRangeInSec, END_OF_DAY_IN_SEC)
        : END_OF_DAY_IN_SEC;
    return props.minRangeInSec && props.minRangeInSec > 0
      ? Math.min(props.minRangeInSec, maxSec)
      : maxSec;
  };

  // 시·분·초가 모두 readonly인가 (사용자가 시간을 직접 편집할 수 없는 상태)
  const isTimeFullyReadonly = () =>
    Boolean(
      props.readonlyTimes?.hour && props.readonlyTimes?.minute && props.readonlyTimes?.second,
    );

  // 내일 경계 더블클릭 시 시작 시각(HH:mm:ss).
  // 하루 전체(24h)가 maxRange를 초과하면 시작 시각을 maxRange만큼 뒤로 당겨
  // 정확히 maxRange 범위(시작 시각 ~ 내일 00:00:00)로 맞춘다.
  const calcTomorrowBoundaryStartTime = () => {
    const fullDayInSec = END_OF_DAY_IN_SEC + 1;
    if (props.maxRangeInSec && props.maxRangeInSec > 0 && props.maxRangeInSec < fullDayInSec) {
      return getNow()
        .startOf('day')
        .add(fullDayInSec - props.maxRangeInSec, 'second')
        .format('HH:mm:ss');
    }
    return '00:00:00';
  };

  watch(endDate, (newEnd, oldEnd) => {
    if (!isRangeSelectionComplete(newEnd, oldEnd)) {
      return;
    }

    if (!isSameDaySelection(newEnd!, startDate.value)) {
      return;
    }
    if (isTomorrowBoundarySelection(newEnd!)) {
      startDate.value = getNow().startOf('day').toDate();
      timePickerValues.value = [calcTomorrowBoundaryStartTime(), '00:00:00'];
      return;
    }

    // 시간 전체가 readonly면 사용자가 고정한 시간을 보존한다 (자동 변경 방지)
    if (isTimeFullyReadonly()) {
      return;
    }

    const endTime = dayjs(startDate.value)
      .startOf('day')
      .add(calcSameDayRangeSec(), 'second')
      .format('HH:mm:ss');
    timePickerValues.value = ['00:00:00', endTime];
  });

  // 종료일이 내일 경계이면 종료 시각을 00:00:00으로 클램프한다.
  // immediate: true이므로 마운트 시점에도 동작하여, dateTimePeriod으로 전달된
  // 종료 시각이 00:00:00이 아니면 자동 보정된다. (예: '내일 15:00:00' → '내일 00:00:00')
  watch(
    isEndDateTomorrowBoundary,
    (isBoundary) => {
      if (isBoundary && timePickerValues.value[1] !== '00:00:00') {
        timePickerValues.value = [timePickerValues.value[0], '00:00:00'];
      }
    },
    { immediate: true },
  );

  const onClickCancel = () => {
    emit('clickCancel');
  };

  const onClickOk = () => {
    emit('clickOk', [
      startDateTime.value.format('YYYY.MM.DD HH:mm:ss'),
      endDateTime.value.format('YYYY.MM.DD HH:mm:ss'),
    ]);
  };

  const timePickerRef = ref<ComponentPublicInstance<{ root: HTMLElement }>>();

  onMounted(() => {
    nextTick(() => {
      const hourInput = timePickerRef.value?.$el?.querySelector('input[name="hour"]');
      if (hourInput) {
        hourInput.focus();
      }
    });
  });

  return {
    t,
    rangeCalendarRef,
    startDate,
    endDate,
    startDateString,
    endDateString,
    onlyShowDate,
    timePickerValues,
    endTimeReadonlyTimes,
    timeValidationInfo,
    isRangeInToday,
    onClickResetDateTimePicker,
    onClickCancel,
    onClickOk,
    timePickerRef,
  };
};
